/*
 * OverlayAddMapping.tsx
 * Author: fwunderlich
 * Date: 04.12.2023
 *
 * Copyright: DMT GmbH & Co. KG
 */

import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AutoComplete} from 'primereact/autocomplete';
import {MappingInputs} from './MappingInputs';
import {Button} from 'primereact/button';
import {useGetParcelQuery} from '../../redux/rtk/injectedParcelApi';
import {useGetPersonQuery} from '../../redux/rtk/injectedPersonApi';
import {getPersonLabel, LandRegister, Parcel, Person} from '../functions/GlobalData';
import {MappingType} from '../../redux/slices/MappingSlice';
import {useGetLandRegisterQuery} from '../../redux/rtk/injectedLandregisterApi';
import {useGetCorrespondenceQuery} from '../../redux/rtk/injectedCorrespondenceApi';

type Props = {
    id: string,
    projectId: string,
    id_type: string,
    type: MappingType,
    activeMappings: string[],
    onSubmit: (selectedItem: any, mapping: any) => void,
}

/**
 * Form for editing and creating of users
 * @param props
 * @constructor
 */
export const OverlayAddMapping = (props: Props): JSX.Element => {

    const {t} = useTranslation(['common', 'globalData', 'toasts']);

    const [autoCompleteItems, setAutoCompleteItems] = useState<any>([]);

    const [placeholder, setPlaceholder] = useState<string>('');
    const [buttonLabel, setButtonLabel] = useState<string>('');

    const [selectedAutoCompleteItem, setSelectedAutoCompleteItem] = useState<any>(undefined);
    const [selectedItem, setSelectedItem] = useState<any>(undefined);

    const [buttonEnabled, setButtonEnabled] = useState<boolean>(false);

    const [mappingInputs, setMappingInputs] = useState<any>({});

    const [skipParcel, setSkipParcel] = useState(true);
    const [skipPerson, setSkipPerson] = useState(true);
    const [skipLandRegister, setSkipLandRegister] = useState(true);

    const [hasValidationError, setHasValidationError] = useState<boolean>(false);

    const [grHa, setGrHa] = useState<number>(0);
    const [grA, setGrA] = useState<number>(0);
    const [grM2, setGrM2] = useState<number>(0);

    const parcels = useGetParcelQuery({
        // projectId: props.projectId,
        projectId: props.projectId,
        personId: '',
        commonData: true,
        mapping:{mapping:true}
    }, {skipParcel});

    const persons = useGetPersonQuery({
        // projectId: props.projectId,
        projectId: props.projectId,
        personId: '',
        commonData: true,
        mapping:{mapping:true}
    }, {skipPerson});

    const landRegisters = useGetLandRegisterQuery({
        // projectId: props.projectId,
        projectId: props.projectId,
        grundbuchId: '',
        commonData: true,
        mapping:{mapping:true}
    }, {skipLandRegister});

    useEffect(() => {
        switch (props.id_type) {
            case 'parcel':
                setSkipParcel(false);
                setPlaceholder(t('globalData:global.mapping.searchMappingParcel'));
                setButtonLabel(t('globalData:global.mapping.addMappingParcel'));
                break;
            case 'person':
                setSkipPerson(false);
                setPlaceholder(t('globalData:global.mapping.searchMappingPerson'));
                setButtonLabel(t('globalData:global.mapping.addMappingPerson'));
                break;
            case 'landRegister':
                setSkipLandRegister(false);
                setPlaceholder(t('globalData:global.mapping.searchMappingLandRegister'));
                setButtonLabel(t('globalData:global.mapping.addMappingLandRegister'));
                break;
            case 'correspondence':
                setButtonLabel(t('globalData:global.mapping.addMappingCorrespondence'));
                break;

            case 'contract':
                setButtonLabel(t('globalData:global.mapping.addMappingContract'));
                break;
        }
    }, []);

    useEffect(() => {
        if (selectedItem) {
            if (props.id_type === 'parcel') {
                let groesseNr = (selectedItem as Parcel).kataster_groesse;

                if (groesseNr) {
                    let groesse = groesseNr.toString();
                    let decimal = '';
                    if (groesse.indexOf('.') >= 0) {
                        let split = groesse.split('.');
                        if (split.length === 2) {
                            groesse = split[0];
                            decimal = split[1];
                        }
                    }

                    if (groesse) {
                        let haEnd = 0;
                        let aEnd = 0;
                        let m2End = 0;

                        console.log(groesse);

                        if (groesse.length >= 5) {
                            haEnd = groesse.length - 4;
                            let value = parseInt(groesse.substring(0, haEnd));
                            console.log(value);
                            setGrHa(value);
                        }

                        if (groesse.length >= 4) {
                            aEnd = haEnd + 2;
                            let value = parseInt(groesse.substring(haEnd, aEnd));
                            console.log(value);
                            setGrA(value);
                        } else if (groesse.length >= 3) {
                            aEnd = haEnd + 1;
                            let value = parseInt(groesse.substring(haEnd, aEnd));
                            console.log(value);
                            setGrA(value);
                        }

                        if (groesse.length >= 2) {
                            m2End = aEnd + 2;
                            let value = parseInt(groesse.substring(aEnd, m2End));
                            console.log(value);
                            setGrM2(value);
                        } else {
                            m2End = aEnd + 1;
                            let value = parseInt(groesse.substring(aEnd, m2End) + (decimal ? '.' + decimal : ''));
                            console.log(value);
                            setGrM2(value);
                        }
                    }
                }
            }
        }
    }, [selectedItem]);

    useEffect(() => {
        let result = false;

        if (selectedItem) {
            result = true;
        } else {
            if (mappingInputs.erbengemeinschaft) {
                let allSet = true;
                for (let i = 0; i < mappingInputs.erbengemeinschaft.length; i++) {
                    let erben = mappingInputs.erbengemeinschaft[i];
                    if (!erben.person || !erben.person.value) {
                        allSet = false;
                        break;
                    }
                }
                result = allSet;
            } else if (mappingInputs.korrespondenz_type_id) {
                result = true;
            } else if (mappingInputs.vertrag_type_id) {
                result = true;
            }
        }

        setButtonEnabled(result);
    }, [selectedItem, mappingInputs]);

    const setMappings = (mapping: any) => {
        setMappingInputs((prevState: any) => {
            let temp = {...prevState};

            for (let i = 0; i < Object.keys(mapping).length; i++) {
                let key = Object.keys(mapping)[i];
                let value = mapping[key];
                temp[key] = value;
            }

            return temp;
        });
    };

    const onButtonClick = () => {
        props.onSubmit(selectedItem, mappingInputs);
    };

    const onAutoComplete = (e: any) => {
        switch (props.id_type) {
            case 'parcel':
                if (parcels.data) {
                    for (let i = 0; i < parcels.data.length; i++) {
                        let item = parcels.data[i];

                        if (item.flstkennz === e.value.value) {
                            setSelectedItem(item);
                            break;
                        }
                    }
                }
                break;
            case 'person':
                if (persons.data) {
                    for (let i = 0; i < persons.data.length; i++) {
                        let item = persons.data[i];

                        if (item.person_id === e.value.value) {
                            setSelectedItem(item);
                            break;
                        }
                    }
                }
                break;
            case 'landRegister':
                if (landRegisters.data) {
                    for (let i = 0; i < landRegisters.data.length; i++) {
                        let item = landRegisters.data[i];

                        if (item.grundbuch_id === e.value.value) {
                            setSelectedItem(item);
                            break;
                        }
                    }
                }
                break;
        }

        setSelectedAutoCompleteItem(e.value);
    };

    const searchItems = (event: any) => {
        let temp = [];
        let key = event.query.toLowerCase();

        switch (props.id_type) {
            case 'parcel':
                if (parcels.data) {
                    temp = parcels.data.filter((item: Parcel) => {
                        return parcelSearch(key, item) && props.activeMappings.indexOf(item.flurstueck_id) < 0;
                    }).map((item: Parcel) => {
                        return {
                            label: `${item.flstkennz} (${item.kreis_name} / ${item.gemeinde_name} / ${item.gemarkung_name})`,
                            value: item.flstkennz
                        };
                    });
                }
                break;
            case 'person':
                if (persons.data) {
                    temp = persons.data.filter((item: Person) => {
                        return (personSearch(key, item) && props.activeMappings.indexOf(item.person_id) < 0);
                    }).map((item: Person) => {
                        return {label: getPersonLabel(item), value: item.person_id};
                    });
                }
                break;
            case 'landRegister':
                if (landRegisters.data) {
                    temp = landRegisters.data.filter((item: LandRegister) => {
                        return (`${item.amtsgericht} ${item.blatt} ${item.bezirk}`).toLowerCase().indexOf(key) >= 0 && props.activeMappings.indexOf(item.grundbuch_id) < 0;
                    }).map((item: LandRegister) => {
                        return {label: `${item.amtsgericht} ${item.blatt} ${item.bezirk}`, value: item.grundbuch_id};
                    });
                }
                break;
        }

        setAutoCompleteItems(temp);
    };


    const parcelSearch = (key: string, parcel: Parcel): boolean => {
        let results = [];

        let keys = key.split(' ');

        for (let i = 0; i < keys.length; i++) {
            let k = formatValue(keys[i]);

            let tempResult = false;

            if (parcel.flstkennz) {
                tempResult = tempResult || formatValue(parcel.flstkennz).indexOf(k) >= 0;
            }

            if (parcel.kreis_name) {
                tempResult = tempResult || formatValue(parcel.kreis_name).indexOf(k) >= 0;
            }

            if (parcel.kreis) {
                tempResult = tempResult || formatValue(parcel.kreis).indexOf(k) >= 0;
            }

            if (parcel.gemeinde_name) {
                tempResult = tempResult || formatValue(parcel.gemeinde_name).indexOf(k) >= 0;
            }

            if (parcel.gemeinde) {
                tempResult = tempResult || formatValue(parcel.gemeinde).indexOf(k) >= 0;
            }

            if (parcel.gemarkung_name) {
                tempResult = tempResult || formatValue(parcel.gemarkung_name).indexOf(k) >= 0;
            }

            results.push(tempResult);
        }

        return results.indexOf(false) < 0;
    };

    const personSearch = (key: string, person: Person): boolean => {
        let results = [];

        let keys = key.split(' ');

        for (let i = 0; i < keys.length; i++) {
            let k = formatValue(keys[i]);

            let tempResult = false;

            if (person.name) {
                tempResult = tempResult || formatValue(person.name).indexOf(k) >= 0;
            }

            if (person.vorname) {
                tempResult = tempResult || formatValue(person.vorname).indexOf(k) >= 0;
            }

            if (person.adresse) {
                for (let i = 0; i < person.adresse.length; i++) {
                    let adresse = person.adresse[i];

                    if (((person.ist_juristisch && adresse.adresse_type_id === 6) || (!person.ist_juristisch && adresse.adresse_type_id === 1)) && adresse.ladungsadresse) {

                        if (adresse.ort) {
                            tempResult = tempResult || formatValue(adresse.ort).indexOf(k) >= 0;
                        }

                        if (adresse.strassehausnummer) {
                            tempResult = tempResult || formatValue(adresse.strassehausnummer).indexOf(k) >= 0;
                        }

                        break;
                    }
                }
            }

            results.push(tempResult);
        }

        return results.indexOf(false) < 0;
    };

    const formatValue = (value: string) => {
        let result = value;

        result = result.replace(',', '');
        result = result.replace('.', '');
        result = result.replace('/', '');
        result = result.toLowerCase();

        return result;
    };

    return (<div>
        <MappingInputs id={props.id} projectId={props.projectId} mappingType={props.type} onChange={setMappings}
                       mappings={{
                           map_person_type_id: 0,
                           map_anteileigentuemer: '',
                           map_miteigentumsanteil: '',
                           map_artrechtsgemeinschaft: '',
                           map_bvnr: 0,
                           map_gr_ha: grHa,
                           map_gr_a: grA,
                           map_gr_m2: grM2,
                           map_korrespondenz_type_id: '',
                           map_datum_soll: '',
                           map_datum_ist: '',
                           map_vertrag_type_id: '',
                           map_flaeche_erwerb: 0,
                           map_flaeche_dinglich: 0,
                           map_flaeche_temp: 0
                       }}
                       overlay={true} setValidationError={setHasValidationError}
                       activeMappings={props.activeMappings}
                       autoCompleteComponent={<div className={'grid'}>
                           <AutoComplete inputClassName={'w-full'}
                                         className={'col-12 w-full mb-2'}
                                         value={selectedAutoCompleteItem}
                                         suggestions={autoCompleteItems}
                                         field={'label'}
                                         completeMethod={searchItems}
                                         onChange={onAutoComplete}
                                         placeholder={placeholder}
                           ></AutoComplete></div>}/>
        <Button
            className={'w-full mt-3'}
            label={buttonLabel}
            disabled={!buttonEnabled || hasValidationError}
            onClick={onButtonClick}/>
    </div>);
};
